/**
 * <semi360-header> — the drop-in portal chrome for Semi360 product apps.
 *
 * Framework-agnostic Web Component. Compiled by scripts/build-embed.mjs to a
 * single self-contained file served at /embed/header.js. A product app includes:
 *
 *   <script src="https://app.semi360.ai/embed/header.js"></script>
 *   <semi360-header product="design360" org="Acme" user="NR"></semi360-header>
 *
 * Attributes:
 *   product   current product id (design360 | design | comp360 | comp | ...)
 *   org       org name shown in the chip / account menu
 *   user      avatar initials (fallback when not signed in / no session)
 *   base      Hub base URL (defaults to the origin the script was loaded from,
 *             else https://app.semi360.ai)
 *
 * Ported from mockups/semi360-portal-chrome(1).html: the slim top bar, the
 * product switcher panel and the account menu, with the same tokens and layout.
 * Uses Shadow DOM so host-page CSS cannot clash. On connect it calls the Hub's
 * /api/session (credentialed, cross-origin) to learn who is signed in and shows
 * "Sign in" when anonymous.
 */

// Registers <semi360-loader> — the suite's one loading indicator (Sami's mark,
// working). Bundled into header.js so every product that mounts the header can
// render a loading state with no extra script tag.
import "./loader";
import { PRODUCTS, switcherProducts, normProduct, detectHubBase, type HubLevel, type ProductInfo } from "./products";
import { ensureTokens } from "./tokens";
// The lockup wordmark (white variant) — one module, shared with
// <semi360-footer>, so the mark in the footer cannot drift from the mark in
// the bar. See embed/logo.ts.
import { WORDMARK_LT } from "./logo";

interface SessionUser {
  name?: string;
  initials?: string;
  org?: string;
  accountType?: string;
  role?: string;
}
/** The org block the Hub returns alongside the user — carries the plan. */
interface SessionOrg {
  id?: string;
  name?: string;
  plan?: string;
  companyType?: string;
  /** The licence's company-type bundle (oem/ems/distributor/manufacturer/…),
   *  read from the token so the switcher, rail, Home and Sami's offers can
   *  adjust to the org's bundle without a second fetch (P19 item 4). */
  bundle?: string;
  /** Whether this org may hold Ecom360 today — distributor bundle or an
   *  access-code add-on (P07 access codes). Computed server-side from the
   *  same licensing module the product JWT uses, so the switcher never
   *  disagrees with what a product's token says (P19 item 5). */
  ecom360?: boolean;
}
type SessionResp =
  | { authenticated: true; user: SessionUser; org?: SessionOrg }
  | { authenticated: false };

/**
 * This deployment's Hub base URL, inlined at build time by
 * scripts/build-embed.mjs from NEXT_PUBLIC_HUB_BASE_URL; "" when unset.
 * Last resort only — detectBase() infers the base from the <script src>
 * whenever it can, which is what makes one bundle work on any zone.
 */
declare const __HUB_BASE__: string;

const DEFAULT_BASE = (__HUB_BASE__ || "https://hub.dev.semi360.ai").replace(/\/+$/, "");

/**
 * Per-deployment product URL overrides, inlined at build time by
 * scripts/build-embed.mjs from NEXT_PUBLIC_PRODUCT_URLS ("key=url,key=url").
 * Always defined — esbuild substitutes "" when the variable is unset, because an
 * undefined identifier here would bundle clean and throw ReferenceError in every
 * product's page.
 */
declare const __PRODUCT_URLS__: string;

/**
 * Where each product actually lives, when it is not `<product>.<hub zone>`.
 *
 * productUrl() derives the subdomain from the Hub's own hostname, which is right
 * for the hosted zones (hub.dev.semi360.ai -> rfq360.dev.semi360.ai) and wrong
 * for any deployment that separates products by PORT rather than by name. The
 * local checkout is one: the Hub is bom.dev.bom360.ai:29002 and RFQ360 is
 * bom.dev.bom360.ai:29000 — the same host. Derivation there produced
 * https://rfq360.bom.dev.bom360.ai, a name that does not resolve, and silently,
 * because `new URL(base).hostname` drops the port that was the only difference.
 *
 * Keys are canonical product keys (rfq360, not rfq). Empty in the hosted builds,
 * so nothing about them changes.
 */
const PRODUCT_URLS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const pair of (__PRODUCT_URLS__ || "").split(",")) {
    const i = pair.indexOf("=");
    if (i < 1) continue;
    const key = pair.slice(0, i).trim();
    const url = pair.slice(i + 1).trim();
    if (key && url) out[key] = url.replace(/\/+$/, "");
  }
  return out;
})();

// Product marks — id → { colour, path }. Ported from the chrome mockup PMARK.
const PMARK: Record<string, { c: string; d: string }> = {
  comp: { c: "24D0C0", d: '<circle cx="11" cy="11" r="6.2"/><path d="M19.5 19.5L15.4 15.4"/>' },
  design: { c: "5F47C9", d: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.4"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.4"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.4"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.4"/>' },
  sample: { c: "8A6CFF", d: '<path d="M12 2.8l8.2 4.6v9.2L12 21.2 3.8 16.6V7.4z"/><path d="M3.8 7.4L12 12l8.2-4.6M12 12v9.2"/>' },
  bom: { c: "39B6FF", d: '<path d="M4 5.5h16M4 12h16M4 18.5h10"/><path d="M4 5.5v13"/>' },
  xref: { c: "12876B", d: '<path d="M4 8h10l-3-3M20 16H10l3 3"/>' },
  rfq: { c: "D99A2B", d: '<path d="M21.5 2.5L10.8 13.2M21.5 2.5l-6.8 19-3.9-8.3-8.3-3.9z"/>' },
  qbuy: { c: "6E4FD6", d: '<circle cx="9.5" cy="20" r="1.3"/><circle cx="18" cy="20" r="1.3"/><path d="M2.5 3h3l2.6 12.4h11.2L21.5 7H7"/>' },
  xcess: { c: "0E9A8D", d: '<path d="M12 3l9 4.6-9 4.6-9-4.6z"/><path d="M3 12.6l9 4.6 9-4.6M3 17l9 4.6 9-4.6"/>' },
  ecom: { c: "B44FC9", d: '<path d="M3.4 8.6L5 3.6h14l1.6 5"/><path d="M3.4 8.6a2.6 2.6 0 005.2 0 2.6 2.6 0 005.2 0 2.6 2.6 0 005.2 0"/><path d="M4.8 10.8V20.4h14.4V10.8"/>' },
  chat: { c: "2E8FD6", d: '<path d="M21 14.5a2.6 2.6 0 01-2.6 2.6H8.2L3 21.4V5.1A2.6 2.6 0 015.6 2.5h12.8A2.6 2.6 0 0121 5.1z"/>' },
};

const PLAIN: Record<string, string> = {
  design: "Design360", sample: "Sample360", bom: "Optimize360", xref: "Xref360", comp: "Comp360",
  xcess: "Xcess360", rfq: "RFQ360", qbuy: "Qbuy360", ecom: "ECom360", chat: "Chat360", hub: "Hub",
};

// Short pill text (fits the cell); the full phrasing is the tooltip.
// One vocabulary, read as a progress ladder. "External" and "Standalone" said
// different things about products in the same state — neither started — and a
// reader had to know the difference to see they were equivalent. They are both
// "Not started" now; whether a product will eventually live on the platform is
// a roadmap question, not a status.
const HUB_LABEL: Record<HubLevel, string> = {
  ready: "Complete",
  wired: "Integrated",
  mockup: "In progress",
  partial: "In progress",
  none: "Not started",
  ext: "Not started",
};

function pmark(id: string, size: number): string {
  const m = PMARK[id] || PMARK.comp;
  const s = size || 22;
  const inner =
    `<svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="1.9" stroke-linecap="round" ` +
    `stroke-linejoin="round" width="${Math.round(s * 0.58)}" height="${Math.round(s * 0.58)}">${m.d}</svg>`;
  return `<span class="pmark" style="width:${s}px;height:${s}px;background:#${m.c}">${inner}</span>`;
}

const STYLE = `
:host{ all:initial; display:block; width:100%; position:sticky; top:0; z-index:90; font:inherit; }
*{box-sizing:border-box;margin:0;padding:0;font-family:"Inter","Segoe UI",-apple-system,BlinkMacSystemFont,sans-serif}
button{font:inherit;cursor:pointer;border:0;background:none;color:inherit}
:root{}
.wrap{ --brand-ink:#191334;--navy:#191334;--brand-violet:#5F47C9;--brand-teal:#24D0C0;--teal-br:#24D0C0;
  --teal:#12876B;--ink:#191334;--muted:#6E827F;--line:#D8E4E2;--bg-2:#E0FCF8;--bg-row-hover:#F0FAF8;
  --success:#12876B;--danger:#C50F1F;--font-mono:"Cascadia Mono",ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;--radius-md:6px; }
.sp{flex:1}
.pmark{display:inline-grid;place-items:center;border-radius:7px;flex:none}
/* The notification bell is a fixed overlay pinned to the right edge (the
   Hub mounts it on document.body so a shell re-render cannot delete it),
   so the bar reserves its width — 38px plus the gap — or the account
   button sits underneath it. */
.top{position:sticky;top:0;z-index:60;height:46px;background:var(--navy);display:flex;align-items:center;gap:9px;padding:0 11px;border-bottom:1px solid #241F45}
.top[data-bell="true"]{padding-right:58px}
.menubtn{display:none;align-items:center;justify-content:center;width:30px;height:30px;border-radius:8px;flex:none;color:#EAF3F1;background:transparent;border:none}
.menubtn:hover{background:rgba(255,255,255,.08)}
@media (max-width:1000px){:host([data-menu]) .menubtn{display:inline-flex}}
/* One focus ring for the whole chrome. The bar is --brand-ink, so the ring is
   --brand-violet-300 rather than --brand-violet: the contract colour at the
   contract width, in the tint that is actually legible on navy. The search
   input clears the UA outline for its frame, so it is named here too. */
button:focus-visible,a:focus-visible,input:focus-visible,
.gsearch:focus-within{outline:2px solid var(--brand-violet-300);outline-offset:1px}
.brandbtn{display:flex;align-items:center;padding:3px 8px 3px 5px;border-radius:8px;flex:none}
.brandbtn:hover{background:rgba(255,255,255,.08)}
.brandbtn:hover .brandsub{color:var(--teal-br)}
.lockup-svg{display:block}
.brandsub{font-size:11px;color:#8FA9A5;padding-left:8px;margin-left:7px;border-left:1px solid rgba(255,255,255,.2);white-space:nowrap}
.iddiv{width:1px;height:17px;background:rgba(255,255,255,.2);flex:none}
.prodbtn{display:flex;align-items:center;gap:7px;padding:4px 9px 4px 5px;border-radius:100px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.13);color:#EAF3F1;cursor:pointer;transition:.13s;min-width:0}
.prodbtn:hover{background:rgba(255,255,255,.15)}
.prodbtn[data-open="true"]{background:var(--teal-br);border-color:var(--teal-br);color:#0B1F1D}
.prodbtn .pn{font-size:12.5px;font-weight:700;white-space:nowrap}
.prodbtn .cv{width:11px;height:11px;opacity:.6;flex:none}
.gsearch{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.18);border-radius:var(--radius-md);padding:0 10px;height:30px;width:230px}
.gsearch svg{width:15px;height:15px;color:rgba(255,255,255,.6);flex:none}
.gsearch input{flex:1;min-width:0;background:none;border:0;color:#fff;font:inherit;font-size:13px}
.gsearch input:focus-visible{outline:none}
.gsearch input::placeholder{color:rgba(255,255,255,.5)}
.gsearch .gkey{font-family:var(--font-mono);font-size:11px;color:rgba(255,255,255,.4);border:1px solid rgba(255,255,255,.2);border-radius:4px;padding:0 5px;line-height:16px}
/* Sami's seat in the chrome: the Semi360 zero in the bright focus teal, with a
   quiet breathing glow. Clicking asks the page's copilot dock to open; where
   no dock is mounted it deep-links to the workroom instead. */
.flowback{display:inline-flex;align-items:center;height:26px;padding:0 10px;margin-left:10px;
  border:1px solid rgba(255,255,255,.22);border-radius:100px;font-size:11.5px;font-weight:600;
  color:rgba(255,255,255,.88);text-decoration:none;white-space:nowrap}
.flowback:hover{background:rgba(255,255,255,.1);border-color:rgba(255,255,255,.4)}
.samibtn{position:relative;width:30px;height:30px;border-radius:50%;flex:none;display:grid;place-items:center;
  background:rgba(36,208,192,.1);border:1px solid rgba(36,208,192,.5);transition:.13s}
.samibtn:hover{background:rgba(36,208,192,.22);border-color:var(--brand-teal);transform:translateY(-1px)}
.samibtn:active{transform:scale(.92)}
.samibtn svg{width:19px;height:19px;transition:transform .45s cubic-bezier(.3,1.6,.5,1)}\n.samibtn .eye{transform-origin:center;transform-box:fill-box;animation:s360hbblink 4.8s ease-in-out infinite}\n.samibtn .eye:last-of-type{animation-delay:.05s}\n@keyframes s360hbblink{0%,91%,100%{transform:scaleY(1)}94%{transform:scaleY(.12)}97%{transform:scaleY(1)}}
.samibtn:hover svg{transform:rotate(180deg) scale(1.1)}
/* periodic sonar ping, so the chrome quietly says "someone lives here" */
.samibtn::after{content:"";position:absolute;inset:-1px;border-radius:50%;
  border:1.5px solid rgba(36,208,192,.75);opacity:0;pointer-events:none;
  animation:s360hbping 8s ease-out 3s infinite}
@keyframes s360hbping{0%,80%{transform:scale(1);opacity:0}82%{opacity:.85}100%{transform:scale(2);opacity:0}}
@media(prefers-reduced-motion:reduce){.samibtn::after{animation:none}.samibtn:hover svg{transform:none}.samibtn .eye{animation:none}}
.orgchip{display:flex;align-items:center;gap:7px;padding:4px 9px;border-radius:100px;background:rgba(255,255,255,.06);font-size:11.5px;color:#B4C7C4;white-space:nowrap}
.planchip{font-family:var(--font-mono);font-size:8.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:2px 7px;border-radius:100px;background:rgba(255,255,255,.14);color:rgba(255,255,255,.86);margin-left:2px}
.typebadge{font-family:var(--font-mono);font-size:8.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding:2px 7px;border-radius:100px;background:var(--teal-br);color:#0B1F1D}
.userbtn{display:flex;align-items:center;gap:7px;padding:3px 8px 3px 3px;border-radius:100px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.13);color:#EAF3F1;flex:none;transition:.13s}
.userbtn:hover{background:rgba(255,255,255,.15)}
.userbtn[data-open="true"]{background:var(--teal-br);border-color:var(--teal-br);color:#0B1F1D}
.ub-av{width:26px;height:26px;border-radius:50%;background:var(--brand-violet);color:#fff;font-size:10px;font-weight:700;display:grid;place-items:center;flex:none}
.userbtn[data-open="true"] .ub-av{background:var(--brand-ink)}
.ub-n{font-size:12px;font-weight:600;white-space:nowrap}
.userbtn .cv{width:11px;height:11px;opacity:.65;flex:none}
.signinbtn{display:flex;align-items:center;gap:7px;padding:6px 13px;border-radius:100px;background:var(--brand-violet);color:#fff;font-size:12.5px;font-weight:700;flex:none;transition:.13s}
.signinbtn:hover{background:#4F39B0}
@media(max-width:1180px){.ub-n,.userbtn .cv{display:none}.userbtn{padding:3px}}
@media(max-width:900px){.orgchip{display:none}}
@media(max-width:860px){.gsearch{display:none}}
@media(max-width:760px){.brandsub{display:none}}
/* panels */
.pnl{position:fixed;z-index:220;width:314px;max-width:calc(100vw - 20px);max-height:78vh;overflow:auto;background:#fff;border:1px solid var(--line);border-radius:14px;padding:9px;box-shadow:0 20px 56px rgba(10,15,30,.30);display:none}
.pnl[data-open="true"]{display:block}
.pnl-grp{font-family:var(--font-mono);font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:#7E918E;padding:9px 9px 5px;display:flex;align-items:center;gap:7px}
.pnl-note{font-family:inherit;font-size:9.5px;letter-spacing:0;text-transform:none;color:var(--teal);font-weight:600}
.pnl-apps{display:grid;grid-template-columns:1fr 1fr;gap:5px;padding:2px 3px 4px}
.pnl-app{display:flex;align-items:center;gap:8px;padding:7px 8px;border-radius:9px;background:none;border:1px solid transparent;cursor:pointer;text-align:left;min-width:0}
.pnl-app:hover{background:var(--bg-row-hover);border-color:var(--line)}
/* Name on its own line, badge below it — so a longer badge (Hub-ready /
   Standalone / External) can never squeeze the product name out of the cell. */
.pa-txt{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 auto}
.pnl-app .pa-n{font-size:11.5px;font-weight:600;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.pnl-app .pa-t{font-family:var(--font-mono);font-size:7.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--teal);align-self:flex-start;flex:none;white-space:nowrap;padding:1.5px 6px;border-radius:100px;line-height:1.3}
.pnl-app .pa-t.free{color:var(--success)}
/* Hub-readiness ladder. ready = fully done (the last stage, green); wired =
   integration plumbing done, product work remains (teal); partial = integrating
   (violet); none = standalone (amber); ext = external (grey). */
.pnl-app .pa-t.hub-ready{color:var(--success);background:var(--success-bg)}
.pnl-app .pa-t.hub-wired{color:var(--teal-br);background:var(--brand-teal-50)}
.pnl-app .pa-t.hub-mockup{color:var(--brand-violet);background:var(--brand-violet-50)}
.pnl-app .pa-t.hub-partial{color:var(--brand-violet);background:var(--brand-violet-50)}
.pnl-app .pa-t.hub-none{color:var(--fg-3);background:var(--bg-2)}
.pnl-app .pa-t.hub-ext{color:var(--fg-3);background:var(--bg-2)}
.pnl-app[data-state="here"]{background:#E8FAF7;border-color:var(--teal-br)}
.pnl-app[data-state="avail"] .pa-n{color:#41524F;font-weight:500}
/* the .s360-btn look (ui.css) restated inside the shadow root: no ink fill */
.pnl-feet{display:flex;gap:6px;margin-top:6px}
.pnl-foot{flex:1;display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 14px;box-sizing:border-box;border-radius:var(--radius-md);background:var(--bg-card);color:var(--fg-1);font:600 13px/18px var(--font-ui);border:1px solid var(--stroke-1);box-shadow:var(--shadow-2);cursor:pointer}
.pnl-foot:hover{border-color:var(--stroke-2)}
/* Sami is a destination here, not the floating bubble the rail chip opens, so
   it reads as a peer of the Hub in the teal accent. Accent, not a teal CTA
   fill: the owner's rule is teal marks, violet acts. */
.pnl-foot.sami{background:var(--brand-teal-100);color:var(--brand-teal-text);border-color:var(--brand-teal)}
.pnl-foot.sami:hover{border-color:var(--brand-teal-text)}
/* account menu */
.acctmenu{position:fixed;top:52px;right:12px;width:266px;background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:0 18px 48px rgba(10,15,30,.30);padding:8px;display:none;z-index:230}
.acctmenu[data-open="true"]{display:block}
.am-head{padding:11px 11px 12px;border-bottom:1px solid var(--line);margin-bottom:7px}
.am-org{font-weight:700;font-size:13.5px;color:var(--ink)}
.am-user{font-size:11.5px;color:var(--muted);margin-top:2px}
.am-type{margin-top:9px;font-size:10.5px;color:var(--muted);display:flex;align-items:center;gap:7px}
.am-type .typebadge{background:var(--brand-violet);color:#fff}
.am-item{display:flex;align-items:center;gap:8px;width:100%;text-align:left;padding:9px 11px;border-radius:8px;font-size:13px;color:var(--ink)}
.am-item:hover{background:var(--bg-row-hover)}
.am-item .am-meta{margin-left:auto;font-family:var(--font-mono);font-size:9.5px;color:var(--muted);background:var(--bg-2);padding:1px 7px;border-radius:100px}
.am-item.danger{color:var(--danger)}
.am-item.danger:hover{background:#FDE7E9}
`;


const CHEVRON = `<svg class="cv" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>`;

// The Semi360 "zero" from the lockup above, standalone — Sami the copilot's
// icon, always in the wordmark's bright focus teal.
const MARK_TEAL = "#24D0C0"; // drift-ok: the mark's own colour. An SVG fill/stroke ATTRIBUTE cannot read var(); this is --brand-teal.
const SAMI_FACE = `<svg class="face" viewBox="0 0 64 64" aria-hidden="true">
<g fill="${MARK_TEAL}"><rect x="3" y="17" width="9" height="4.5" rx="2.2"/><rect x="3" y="29.5" width="9" height="4.5" rx="2.2"/><rect x="3" y="42" width="9" height="4.5" rx="2.2"/><rect x="52" y="17" width="9" height="4.5" rx="2.2"/><rect x="52" y="29.5" width="9" height="4.5" rx="2.2"/><rect x="52" y="42" width="9" height="4.5" rx="2.2"/></g>
<rect x="10" y="7" width="44" height="50" rx="11" fill="#191334" stroke="${MARK_TEAL}" stroke-width="2"/>
<path d="M26.5 7a5.5 5.5 0 0 0 11 0" fill="#0B1F1D" stroke="${MARK_TEAL}" stroke-width="1.4"/>
<circle class="eye" cx="25" cy="30" r="4.6" fill="${MARK_TEAL}"/>
<circle class="eye" cx="39" cy="30" r="4.6" fill="${MARK_TEAL}"/>
<path d="M25.5 42.5q6.5 5 13 0" stroke="${MARK_TEAL}" stroke-width="2.4" fill="none" stroke-linecap="round"/>
</svg>`;

const SAMI_ZERO = `<svg viewBox="792 -3 203 203" fill="${MARK_TEAL}" aria-hidden="true"><path d="M892.898 0C838.622 0 794.627 44.1794 794.627 98.6843C794.627 153.189 838.622 197.353 892.898 197.353C947.175 197.353 991.169 153.174 991.169 98.6843C991.169 44.1948 947.175 0 892.898 0ZM892.898 132.923C874.096 132.923 858.803 117.566 858.803 98.6843C858.803 79.803 874.096 64.4303 892.898 64.4303C911.7 64.4303 926.993 79.803 926.993 98.6843C926.993 117.566 911.7 132.923 892.898 132.923ZM945.382 98.6843C945.382 69.5699 921.89 45.9645 892.898 45.9645C873.56 45.9645 856.657 56.4746 847.555 72.1089C853.087 44.718 877.268 24.0363 906.138 24.0363C939.084 24.0363 965.9 50.9656 965.9 84.0502C965.9 111.056 948.048 133.939 923.576 141.448C936.785 131.877 945.382 116.288 945.382 98.6843ZM892.898 178.887C848.858 178.887 813.016 142.91 813.016 98.6843C813.016 71.8935 826.163 48.1188 846.329 33.5462C834.883 47.1955 827.987 64.815 827.987 84.0502C827.987 127.399 862.971 162.53 906.138 162.53C928.02 162.53 947.788 153.512 961.977 138.955C948.109 162.822 922.335 178.887 892.898 178.887Z"/></svg>`;

class Semi360Header extends HTMLElement {
  private root: ShadowRoot;
  private product = "comp";
  private orgName = "";
  private userInitials = "";
  private base = DEFAULT_BASE;
  private session: SessionResp = { authenticated: false };
  /** productKey -> access ("none" | "metered" | "included"), from
   *  /api/entitlements — the SAME rows a product itself is gated by (P19
   *  item 4). null = not fetched yet, or the fetch failed: every caller
   *  treats null as "unknown" and shows everything, never as "nothing is
   *  licensed". A product with no row at all reads the same way (unknown),
   *  not "none" — a product the licence seed does not yet cover is shown,
   *  not hidden. */
  private entitlementAccess: Record<string, string> | null = null;
  private onDocClick = (e: Event) => this.handleOutside(e);
  private onDocKey = (e: KeyboardEvent) => this.handleKey(e);
  private onRailReady = () => { this.dataset.menu = ""; };

  static get observedAttributes() {
    return ["product", "org", "user", "base", "require-auth"];
  }

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  /** Keep the bar on screen. The shadow styles mark the bar sticky, but most
   * hosts wrap this element in a plain div exactly its own height — and a
   * sticky element inside a container it fills scrolls away with it, bar and
   * all. Comp360, sample360 and design360 each discovered this separately and
   * pinned their wrappers by hand; doing it here fixes every host at once.
   * The element pins itself, and a dedicated single-child wrapper is pinned
   * too. A host that already positioned its wrapper is left untouched. */
  private pinChrome() {
    try {
      // The same courtesy the wrapper gets, extended to the element itself: a
      // host page that already positioned this bar is left untouched. The Hub
      // pins it `fixed` in its own stylesheet and pads the shell to match —
      // inlining `sticky` here OVERRODE that page rule (inline beats author
      // CSS), dropped the bar back into flow, and the shell's compensation
      // padding became a 46px blank band under the header. Computed position
      // is read BEFORE any inline write: `fixed`/`absolute` can only come
      // from the host, since the shadow :host rule says sticky.
      const positionedByHost = ["fixed", "absolute"].includes(
        getComputedStyle(this).position,
      );
      if (!positionedByHost) {
        this.style.position = "sticky";
        this.style.top = "0";
        this.style.zIndex = "90";
      }
      const p = this.parentElement;
      if (
        !positionedByHost &&
        p && p !== document.body && p.children.length === 1 &&
        getComputedStyle(p).position === "static"
      ) {
        p.style.position = "sticky";
        p.style.top = "0";
        p.style.zIndex = "90";
      }
    } catch {
      /* a host that objects keeps its own chrome behaviour */
    }
  }

  connectedCallback() {
    // Bare var(), no literal fallbacks (unified CSS standard §4): a fallback
    // that drifts from its token renders one colour while naming another. So
    // the embed brings the palette instead of guessing it.
    this.pinChrome();
    this.readAttrs();
    ensureTokens(this.base);
    this.render();
    document.addEventListener("click", this.onDocClick);
    document.addEventListener("keydown", this.onDocKey);
    if (document.querySelector("semi360-rail")) this.dataset.menu = "";
    document.addEventListener("semi360:rail-ready", this.onRailReady);
    this.applyFavicon();
    this.fetchSession();
  }

  /** One suite, one tab icon. Every product ships whatever favicon its
   * scaffold came with; the header replaces it with the Hub-served Semi360
   * zero so ten tabs read as one product family. Opt out with a
   * `no-favicon` attribute on the header. */
  private applyFavicon() {
    try {
      if (this.getAttribute("no-favicon") !== null) return;
      const href = `${this.base}/embed/favicon.svg`;
      if (document.querySelector(`link[rel~="icon"][href="${href}"]`)) return;
      document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
        .forEach((l) => l.remove());
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/svg+xml";
      link.href = href;
      document.head.appendChild(link);
    } catch {
      /* a page without a head to edit keeps its own icon */
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocClick);
    document.removeEventListener("keydown", this.onDocKey);
    document.removeEventListener("semi360:rail-ready", this.onRailReady);
  }

  attributeChangedCallback() {
    if (!this.root.firstChild) return;
    this.readAttrs();
    this.render();
  }

  private readAttrs() {
    // products.ts's normProduct("") on a missing attribute; header.ts's own
    // prior default (no product attribute -> Comp360) is preserved here.
    this.product = normProduct(this.getAttribute("product")) || "comp";
    this.orgName = this.getAttribute("org") || "";
    this.userInitials = (this.getAttribute("user") || "").slice(0, 2).toUpperCase();
    this.base = (this.getAttribute("base") || this.detectBase()).replace(/\/$/, "");
  }

  /** Infer the Hub base URL from where this script was loaded. See products.ts's detectHubBase. */
  private detectBase(): string {
    return detectHubBase("/embed/header.js", DEFAULT_BASE);
  }

  /** Redirect the whole top-level page to the Hub sign-in, returning here after. */
  private gotoSignin() {
    // Escape any iframe so the Hub sign-in loads top-level; cross-origin framing
    // may block access to window.top, so fall back to this frame.
    let target: Window = window;
    try {
      if (window.top && window.top.location.href) target = window.top;
    } catch {
      target = window;
    }
    target.location.assign(
      `${this.base}/signin?next=${encodeURIComponent(target.location.href)}`,
    );
  }

  /** Central login detection for every product that embeds the header.
   *
   * Resolves the cross-origin Hub session once, then:
   *   - dispatches `semi360:session` (detail = the session) so product apps can
   *     react to signed-in / signed-out without re-fetching, and
   *   - if the `require-auth` attribute is present AND the Hub definitively says
   *     the user is anonymous, redirects the top-level page to the Hub sign-in.
   *
   * A transport error (Hub unreachable) is treated as "unknown", NOT anonymous:
   * we do not redirect on it, so a signed-in user is never ejected by a blip.
   * Products embed with `require-auth` to get login for free — no per-product code. */
  private async fetchSession() {
    let resolved = false; // did the Hub actually answer?
    try {
      const res = await fetch(`${this.base}/api/session`, {
        credentials: "include",
        cache: "no-store",
      });
      if (res.ok) {
        this.session = (await res.json()) as SessionResp;
        resolved = true;
        this.render();
      }
    } catch {
      /* transport error — leave session as-is (unknown), fail open */
    }

    // Tell the host page what we found (only when the Hub answered).
    if (resolved) {
      this.dispatchEvent(
        new CustomEvent("semi360:session", {
          detail: this.session,
          bubbles: true,
          composed: true,
        }),
      );
      const requireAuth = this.getAttribute("require-auth") !== null;
      if (requireAuth && !this.session.authenticated) this.gotoSignin();
      // A signed-in page gets Sami's dock for free: the header is on every
      // product already, so mounting the copilot from here is what puts one
      // assistant on all ten products without a single product deploy.
      if (this.session.authenticated) {
        this.mountCopilot();
        void this.fetchEntitlements();
      }
    }
  }

  /** The org's real product access, for the switcher (P19 item 4).
   *
   * Best-effort and silent: a failed or slow fetch leaves entitlementAccess
   * null, which every reader treats as "unknown" — the switcher falls back to
   * showing every product, exactly today's behaviour, rather than hiding a
   * product on a guess. Re-fetched on every mount (not cached across page
   * loads) since a redeemed access code or a plan change must show up the
   * next time the panel opens, same as the entitlements screen itself. */
  private async fetchEntitlements() {
    try {
      const res = await fetch(`${this.base}/api/entitlements`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) return;
      const body = (await res.json()) as { entitlements?: Array<{ productKey?: string; access?: string }> };
      const rows = Array.isArray(body.entitlements) ? body.entitlements : [];
      const access: Record<string, string> = {};
      for (const row of rows) if (row.productKey) access[row.productKey] = row.access || "none";
      // Read fresh by prodPanelHTML() the next time the switcher opens
      // (toggleProd rebuilds its markup on every open) — no forced re-render
      // needed, and the bar itself never reads this field.
      this.entitlementAccess = access;
    } catch {
      /* transport error — stays null, the switcher fails open */
    }
  }

  /** Mount <semi360-copilot> beside this header, once per page.

   * Guarded on an existing element so a host that mounts its own (the Hub
   * shell does) or two headers on one page cannot stack two docks. The dock
   * resolves its own entitlement and stays hidden when the org's plan lacks
   * the copilot, so this mount is presence, not a promise. Opt out with a
   * `no-copilot` attribute on the header. */
  private mountCopilot() {
    try {
      if (this.getAttribute("no-copilot") !== null) return;
      if (document.querySelector("semi360-copilot")) return;
      const scriptId = "s360-copilot-embed";
      if (!document.getElementById(scriptId)) {
        const s = document.createElement("script");
        s.id = scriptId;
        s.src = `${this.base}/embed/copilot.js`;
        s.async = true;
        document.body.appendChild(s);
      }
      // Safe before the script defines the element: it upgrades in place.
      const el = document.createElement("semi360-copilot");
      const NAME: Record<string, string> = {
        comp: "comp360", design: "design360", sample: "sample360", bom: "bom360",
        xref: "xref360", rfq: "rfq360", qbuy: "qbuy360", xcess: "xcess360",
        ecom: "ecom360", chat: "chat360", hub: "hub",
      };
      el.setAttribute("product", NAME[this.product] || this.product);
      el.setAttribute("base", this.base);
      document.body.appendChild(el);
    } catch {
      /* a page that cannot host the dock still has the header's Sami button */
    }
  }

  private plain(): string {
    return PLAIN[this.product] || this.product;
  }

  // HTML-escape a value before it is interpolated into an innerHTML string.
  // Every user-controlled field in render() (org, name, role, account type)
  // passes through here so a hostile value cannot break out of its element.
  private esc(value: unknown): string {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private render() {
    const authed = this.session.authenticated;
    const u = authed ? this.session.user : undefined;
    const org = (u?.org || this.orgName || "").trim();
    const initials = (u?.initials || this.userInitials || "").trim();
    const name = (u?.name || "").split(" ")[0] || "";
    const acctType = (u?.accountType || "").toUpperCase();
    // The plan belongs in the chrome, next to the org it applies to — not as a
    // stat tile on the Hub home. It is context for everything you do in every
    // product, and every product already mounts this header.
    const plan = (authed ? this.session.org?.plan || "" : "").trim();

    // Arrived from the flow? The handoff carries prj (and bom) in the URL —
    // give the round trip its return door, deep-linked to the exact board.
    // Products only: the Hub renders its own chrome.
    let flowBack = "";
    try {
      const q = new URLSearchParams(location.search);
      // Products with client-side routing (xref360, rfq360…) drop ?prj= on
      // in-app navigation, so the URL is not a reliable source. Every product
      // that arrives from the flow ALSO stashes the ids in sessionStorage
      // under stable keys — fall back to those so "← Flow" survives navigation.
      const ss = (k: string): string | null => {
        try { return sessionStorage.getItem(k); } catch { return null; }
      };
      /* Remember them on THIS origin the first time we see them. The Hub
         cannot do it for us — sessionStorage is per-origin, so anything it
         wrote would be invisible here — and without this the fallback below
         had nothing to read, so the door vanished the moment a client-routed
         product navigated and dropped the query. */
      const keep = (k: string, v: string | null) => {
        try { if (v) sessionStorage.setItem(k, v); } catch { /* no storage */ }
      };
      keep("hub_project_id", q.get("prj"));
      keep("hub_bom_id", q.get("bom"));
      const prj = q.get("prj") || ss("hub_project_id");
      if (prj && location.origin !== this.base) {
        const bom = q.get("bom") || ss("hub_bom_id");
        flowBack = `<a class="flowback" href="${this.base}/hub?s=flow&prj=${encodeURIComponent(prj)}${
          bom ? `&bom=${encodeURIComponent(bom)}` : ""}" title="Back to this board's flow in the Hub">← Flow</a>`;
      }
    } catch { /* no URL API — no door, nothing broken */ }

    const rightSide = authed
      ? `
        <button class="samibtn" id="samiBtn" aria-label="Sami AI — your Semi360 teammate"
          title="Sami AI — your Semi360 teammate">${SAMI_FACE}</button>
        ${org ? `<div class="orgchip">${acctType ? `<span class="typebadge">${this.esc(acctType)}</span>` : ""}<span>${this.esc(org)}</span>${plan ? `<span class="planchip">${this.esc(plan)}</span>` : ""}</div>` : ""}
        <button class="userbtn" id="userBtn" aria-label="Your account" title="Your account, storage, plan and support">
          <span class="ub-av">${this.esc(initials) || "U"}</span>
          ${name ? `<span class="ub-n">${this.esc(name)}</span>` : ""}
          ${CHEVRON}
        </button>`
      : `<a class="signinbtn" href="${this.base}/signin?next=${encodeURIComponent(location.href)}">Sign in</a>`;

    this.root.innerHTML = `
      <style>${STYLE}</style>
      <div class="wrap">
        <header class="top">
          <button class="menubtn" id="menuBtn" aria-label="Open menu" type="button"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg></button>
          <button class="brandbtn" id="brandBtn" aria-label="Semi360.ai Hub home" title="Back to the Hub">
            ${WORDMARK_LT}<span class="brandsub">Hub</span>
          </button>
          <span class="iddiv"></span>
          <button class="prodbtn" id="prodBtn" aria-label="Switch product" title="Switch product">
            ${pmark(this.product, 22)}<span class="pn">${this.plain()}</span>${CHEVRON}
          </button>
          ${flowBack}
          <div class="sp"></div>
          <div class="gsearch" role="search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
            <input type="text" placeholder="Search parts, boards, files…" aria-label="Search">
            <span class="gkey">/</span>
          </div>
          ${rightSide}
        </header>

        <div class="pnl" id="prodPanel" role="menu" aria-label="Switch product"></div>

        <div class="acctmenu" id="acctMenu">
          <div class="am-head">
            <div class="am-org">${this.esc(org) || "Your account"}</div>
            <div class="am-user">${this.esc(u?.name)}${u?.role ? ` · ${this.esc(u.role)}` : ""}</div>
            ${acctType ? `<div class="am-type"><span class="typebadge">${this.esc(acctType)}</span> <span>account type · assigned by your admin</span></div>` : ""}
          </div>
          <button class="am-item" data-feat="hubhome">Hub home</button>
          <button class="am-item" data-feat="storage">Storage &amp; files <span class="am-meta">Drive</span></button>
          <button class="am-item" data-feat="admin">Team &amp; access</button>
          <button class="am-item" data-feat="usage">Usage &amp; limits</button>
          <button class="am-item" data-feat="plans">Plans &amp; billing</button>
          <button class="am-item" data-feat="support">Help &amp; support</button>
          <button class="am-item danger" id="signOut">Sign out</button>
        </div>
      </div>
    `;

    this.wire();
  }

  private wire() {
    const $ = (id: string) => this.root.getElementById(id) as HTMLElement | null;
    this.root.getElementById("menuBtn")?.addEventListener("click", () =>
      this.dispatchEvent(new CustomEvent("semi360:menu", { bubbles: true, composed: true })));
    $("brandBtn")?.addEventListener("click", () => this.goHub("/hub"));
    $("prodBtn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleProd();
    });
    $("userBtn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleAcct();
    });
    $("signOut")?.addEventListener("click", () => this.signOut());
    $("samiBtn")?.addEventListener("click", () => this.openSami());
    this.root.querySelectorAll("[data-hub]").forEach((b) =>
      b.addEventListener("click", () => this.goHub((b as HTMLElement).dataset.hub || "/hub")),
    );
    this.root.querySelectorAll("[data-feat]").forEach((b) =>
      b.addEventListener("click", () => this.openFeature((b as HTMLElement).dataset.feat || "hubhome")),
    );
  }

  /**
   * Open a Hub account feature. On the Hub itself (product="hub") the screens
   * live in the host page, so we close our menu and fire a composed CustomEvent
   * the Hub listens for and opens the feature IN-PLACE. On a *product* app the
   * Hub isn't mounted here, so the same menu item deep-links to the Hub with
   * ?feature=<id>, which opens that screen on load.
   */
  private openFeature(feat: string) {
    this.closeAll();
    if (this.product === "hub") {
      this.dispatchEvent(
        new CustomEvent("semi360:hub-feature", {
          detail: { feature: feat },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    this.goHub(feat === "hubhome" ? "/hub" : `/hub?feature=${encodeURIComponent(feat)}`);
  }

  private goHub(path: string) {
    window.location.assign(`${this.base}${path}`);
  }

  /** Sami from the chrome: ask the page's copilot dock to open (cancelable —
   * a mounted <semi360-copilot> claims it); with no dock on this page, the
   * workroom is one navigation away. */
  private openSami() {
    const ev = new CustomEvent("semi360:copilot-open", {
      bubbles: true, composed: true, cancelable: true,
    });
    const notPrevented = this.dispatchEvent(ev);
    if (notPrevented) this.goHub("/hub?screen=sami");
  }

  /** Product switcher: navigate to that product's own subdomain. */
  private openProduct(id: string) {
    // Let the host page intercept if it wants (cancelable).
    const ev = new CustomEvent("semi360:switch-product", {
      detail: { product: id }, bubbles: true, composed: true, cancelable: true,
    });
    const notPrevented = this.dispatchEvent(ev);
    if (!notPrevented) return; // host handled it

    if (id === this.product) return; // already here
    const url = this.productUrl(id);
    if (url) window.location.assign(url);
    else this.goHub(`/hub?p=${id}`); // unknown/not-yet-hosted → hub routes it
  }

  /** Resolve a product id to its subdomain under the hub's zone. */
  private productUrl(id: string): string | null {
    if (id === "hub") return `${this.base}/hub`;
    // Map short ids to product names; accept either form.
    const NAME: Record<string, string> = {
      comp: "comp360", design: "design360", sample: "sample360", bom: "bom360",
      xref: "xref360", rfq: "rfq360", qbuy: "qbuy360", xcess: "xcess360",
      ecom: "ecom360", chat: "chat360",
    };
    const host = NAME[id] || (/^[a-z]+360$/.test(id) ? id : null);
    if (!host) return null;
    // An explicit URL for this deployment wins over derivation. Checked here,
    // after short ids are normalised, so "rfq" and "rfq360" both find the entry.
    if (PRODUCT_URLS[host]) return PRODUCT_URLS[host];
    // Derive the zone from the hub base, e.g. https://hub.dev.semi360.ai -> dev.semi360.ai
    try {
      const h = new URL(this.base).hostname.replace(/^hub\./, "");
      const proto = new URL(this.base).protocol;
      return `${proto}//${host}.${h}`;
    } catch {
      return null;
    }
  }

  private toggleProd() {
    const p = this.root.getElementById("prodPanel") as HTMLElement;
    const b = this.root.getElementById("prodBtn") as HTMLElement;
    const open = p.dataset.open !== "true";
    this.closeAll("prodPanel");
    p.dataset.open = open ? "true" : "false";
    b.dataset.open = open ? "true" : "false";
    if (open) {
      this.prodPanelHTML();
      const r = b.getBoundingClientRect();
      p.style.top = `${r.bottom + 7}px`;
      p.style.left = `${Math.max(10, r.left)}px`;
      p.style.right = "auto";
    }
  }

  private toggleAcct() {
    const m = this.root.getElementById("acctMenu") as HTMLElement;
    const b = this.root.getElementById("userBtn") as HTMLElement;
    const open = m.dataset.open !== "true";
    this.closeAll("acctMenu");
    m.dataset.open = open ? "true" : "false";
    if (b) b.dataset.open = open ? "true" : "false";
  }

  private closeAll(except?: string) {
    ["prodPanel", "acctMenu"].forEach((id) => {
      if (id === except) return;
      const el = this.root.getElementById(id) as HTMLElement | null;
      if (el) el.dataset.open = "false";
    });
    if (except !== "prodPanel") {
      const pb = this.root.getElementById("prodBtn") as HTMLElement | null;
      if (pb) pb.dataset.open = "false";
    }
    if (except !== "acctMenu") {
      const ab = this.root.getElementById("userBtn") as HTMLElement | null;
      if (ab) ab.dataset.open = "false";
    }
  }

  private handleOutside(_e: Event) {
    // Any click outside the shadow root closes the panels.
    this.closeAll();
  }

  /** Escape closes whichever panel is open and hands focus back to the button
   *  that opened it — the same contract as .s360-dialog, so the chrome behaves
   *  like every other overlay in the suite rather than being the one thing a
   *  keyboard user has to click away from. */
  private handleKey(e: KeyboardEvent) {
    if (e.key !== "Escape") return;
    const opener = this.openPanelOpener();
    if (!opener) return;
    e.stopPropagation();
    this.closeAll();
    opener.focus();
  }

  /** The button behind whichever panel is open, or null when none is. */
  private openPanelOpener(): HTMLElement | null {
    const pairs: [string, string][] = [["prodPanel", "prodBtn"], ["acctMenu", "userBtn"]];
    for (const [panel, btn] of pairs) {
      const el = this.root.getElementById(panel) as HTMLElement | null;
      if (el && el.dataset.open === "true") {
        return this.root.getElementById(btn) as HTMLElement | null;
      }
    }
    return null;
  }

  private prodPanelHTML() {
    const el = this.root.getElementById("prodPanel") as HTMLElement;
    const rank: Record<string, number> = { here: 0, hero: 1, core: 2, free: 3, avail: 4 };
    // ECom360 administers ONE storefront per org — its catalogue, its prices,
    // its live orders — so it is restricted to the licence owner (ecom360's
    // AuthTokenApi refuses anyone else). Offering it in the switcher to a
    // member who cannot open it is a door that only ever says no, so it is
    // hidden rather than shown-and-refused.
    //
    // It is ALSO lean, for distributors (P19 item 5): ECom360 is a
    // distributor storefront, so it is offered only to distributor orgs or
    // orgs that hold it by an access code (P07) — never to every company
    // type by default. org.bundle/org.ecom360 come from the SAME licensing
    // module the product JWT reads (principal_view.session_dict), so this
    // can never show a door the product itself would then refuse. A session
    // read before that field shipped (or anonymous) has `ecom360` undefined;
    // this fails CLOSED (hidden) rather than open, matching "lean" over
    // "offered to everyone until proven otherwise".
    const role = (this.session.user?.role || "").toLowerCase();
    // The org-gated suite only. A product open to everyone (Xcess360, P29) is
    // not reached through the switcher — but on its OWN pages the panel still
    // shows it as "open", so the person can see where they are. It is listed
    // apart from the count, which is about the suite.
    const APPS = switcherProducts();
    const org = this.session.authenticated ? this.session.org : undefined;
    const ecomEntitled = org?.companyType === "distributor" || org?.ecom360 === true;
    // Lean by bundle too (P19 item 4): a product the org's licence reads
    // "none" for is a door that only ever says no, same reasoning as the
    // Ecom360 check above — generalised over every switcher entry instead of
    // one. Driven by /api/entitlements (fetchEntitlements), the exact rows a
    // product itself is gated by, so this can never disagree with what
    // clicking through would actually show. Fails OPEN: no fetch yet, a
    // transport error, or a product with no row at all (a company type the
    // licence seed does not cover, or an org the licence lookup skipped) all
    // show the product rather than hide it — only a definite "none" access
    // hides it.
    const PRODUCT_KEY: Record<string, string> = {
      design: "design360", sample: "sample360", bom: "bom360", xref: "xref360",
      comp: "comp360", rfq: "rfq360", qbuy: "qbuy360", ecom: "ecom360",
    };
    const licensedAccess = this.entitlementAccess;
    const isLicensed = (a: ProductInfo): boolean => {
      if (!licensedAccess) return true;
      const key = PRODUCT_KEY[a.id];
      const access = key ? licensedAccess[key] : undefined;
      return access === undefined || access !== "none";
    };
    const visible = APPS
      .filter((a) => a.id !== "ecom" || (role === "owner" && ecomEntitled))
      .filter(isLicensed);
    const here = APPS.some((a) => a.id === this.product)
      ? null
      : PRODUCTS.find((a) => a.id === this.product) || null;
    const order = (here ? [here, ...visible] : visible).slice().sort((a, b) => {
      const ra = rank[a.id === this.product ? "here" : a.s] ?? 4;
      const rb = rank[b.id === this.product ? "here" : b.s] ?? 4;
      return ra - rb;
    });
    el.innerHTML =
      // Count what is actually listed: "all ten" stopped being true once
      // ECom360 became owner-only, and a header that miscounts the list under
      // it is the kind of small wrongness that makes people distrust the rest.
      `<div class="pnl-grp">Switch product <span class="pnl-note">${
        visible.length === APPS.length ? "all " + APPS.length + " are open to you" : "open to you"
      }</span></div>` +
      `<div class="pnl-apps">` +
      order
        .map((a) => {
          const st = a.id === this.product ? "here" : a.s;
          // The product you are in shows "open"; every other shows its Hub
          // integration level. Unlike the old "for you"/"free" tags, this is a
          // fact about the product, not a guess about the viewer — so it is
          // right for every account, which is why it can be shown.
          const tag =
            st === "here"
              ? `<span class="pa-t">open</span>`
              : `<span class="pa-t hub-${a.hub}" title="${HUB_LABEL[a.hub]}">${HUB_LABEL[a.hub]}</span>`;
          return `<button class="pnl-app" data-state="${st}" data-id="${a.id}" title="${a.n}">${pmark(a.id, 26)}<span class="pa-txt"><span class="pa-n">${a.n}</span>${tag}</span></button>`;
        })
        .join("") +
      `</div>` +
      // The two places that are not products: the Hub itself, and Sami's
      // workroom. The workroom was reachable only from the Hub's own rail, so
      // a person standing in BOM360 who wanted to ask Sami something about the
      // board they were looking at had to go to the Hub first and find it.
      `<div class="pnl-feet">`
      + `<button class="pnl-foot" data-hub="/hub">Go to the Hub</button>`
      + `<button class="pnl-foot sami" data-hub="/hub?screen=sami">Sami's workroom</button>`
      + `</div>`;
    el.querySelectorAll(".pnl-app").forEach((b) =>
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        this.closeAll();
        this.openProduct((b as HTMLElement).dataset.id || "comp");
      }),
    );
    /* Every [data-hub] in the panel, each to its OWN path.

       querySelector (singular) wired only the first — "Go to the Hub" — and
       hardcoded "/hub", so "Sami's workroom" beside it got no listener at all
       and its data-hub="/hub?screen=sami" was never read. Clicking it did
       nothing. wire() already does this correctly for the rest of the header;
       this panel is rebuilt after that runs, which destroys those listeners,
       so it has to repeat the pattern rather than a narrower version of it. */
    el.querySelectorAll("[data-hub]").forEach((b) =>
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        this.closeAll();
        this.goHub((b as HTMLElement).dataset.hub || "/hub");
      }),
    );
  }

  private async signOut() {
    try {
      await fetch(`${this.base}/api/auth/signout`, { method: "POST", credentials: "include" });
    } catch {
      /* ignore */
    }
    // After signing out, land on the Hub itself (hub.semi360.ai in prod), whose
    // front door is the sign-in page — not the marketing site.
    window.location.assign(`${this.base}/`);
  }
}

if (!customElements.get("semi360-header")) {
  customElements.define("semi360-header", Semi360Header);
}
